/*
** Functions to determine valid delivery dates for the CR platform
**
** Handles different delivery methods (FedEx, local delivery, pickup),
** cutoff times, and location-specific constraints.
**
** Unit test these functions with Jest
**
** To run the test, uncomment the last line in this file (module export), then type "npm test" in this directory
** To install jest, type "npm install --save-dev jest"
**
** It is now required to have package.json in the later version.
**
*/

// Configuration constants
const CONFIG = {
  CUTOFF_TIMES: {
    DELIVERY: 15, // 3 PM cutoff for FedEx next-day
    PICKUP: 11    // 11 AM cutoff for same-day pickup/local delivery
  },
  LOCATION_OFF_DAYS: {
    NYC: [0],          // Sunday off
    MIAMI: [6, 0, 1]   // Saturday, Sunday, Monday off
  },
  HOLIDAYS: {
    CR_FIXED: ["1-1", "7-4", "12-25"],  // New Year's, July 4th, Christmas
    FEDEX_FIXED: ["1-2", "7-5", "12-26"],
    CR_FLOATING: [
      "9-6-2021", "9-5-2022", "9-4-2023", "9-2-2024", "9-1-2025",  // Labor Day
      "11-25-2021", "11-24-2022", "11-23-2023", "11-28-2024", "11-27-2025" // Thanksgiving
    ],
    // FEDEX_FLOATING: [
    //   "9-7-2021", "9-6-2022", "9-5-2023", "9-3-2024", "9-2-2025",  // Labor Day
    //   "11-26-2021", "11-25-2022", "12-27-2022", "11-24-2023", "11-29-2024", "11-28-2025" // Thanksgiving
    // ],
    FLOATING: {
      LABOR_DAY: [ "8,0,1" ],
      THANKSGIVING: [ "10,3,4"]
    },
    FEDEX_FLOATING: {
      THE_DAY_AFTER_LABOR_DAY: [ "8,0,2" ],
      THE_DAY_AFTER_THANKSGIVING: [ "10,3,5"]
    }
  },
  FUTURE_DAYS_LIMIT: 90
};

// NYC local delivery ZIP codes
const LOCAL_DELIVERY_ZIPS = [
  "10001", "10002", "10003", "10004", "10005", "10006", "10007", "10008", "10009", "10010",
  "10011", "10012", "10013", "10014", "10016", "10017", "10018", "10019", "10020",
  "10021", "10022", "10023", "10024", "10025", "10026", "10027", "10028", "10029", "10030",
  "10031", "10032", "10033", "10034", "10035", "10036", "10037", "10038", "10039", "10040",
  "10041", "10043", "10044", "10045", "10055", "10060", "10065", "10069", "10075",
  "10080", "10081", "10087", "10090",
  "10101", "10102", "10103", "10104", "10105", "10106", "10107", "10108", "10109", "10110",
  "10111", "10112", "10113", "10114", "10115", "10116", "10117", "10118", "10119", "10120",
  "10121", "10122", "10123", "10124", "10125", "10126", "10128", "10129", "10130",
  "10131", "10132", "10133", "10138",
  "10150", "10151", "10152", "10153", "10154", "10155", "10156", "10157", "10158", "10159",
  "10160", "10162", "10163", "10164", "10165", "10166", "10167", "10168", "10169",
  "10170", "10171", "10172", "10173", "10174", "10175", "10176", "10177", "10178", "10179",
  "10185", "10199", "10203", "10211", "10212", "10213",
  "10242", "11249", "11256", "11258", "11259", "11260", "10261", "10265", "10269",
  "10270", "10271", "10272", "10273", "10274", "10275", "10276", "10277", "10278", "10279",
  "10280", "10281", "10282", "10285", "10286"
];

// State
const state = {
  // Order info
  estOffset: "T00:00:00.000-05:00",
  estOffsetSeconds: 18000000,
  fedExDelivery: false,
  giftCardOnlyOrder: false,
  localDelivery: true,
  pickup: false,
  pickupLocation: "NYC",
  shipToZipcode: "",
  stateInitial: "",

  // Date/time tracking
  today: new Date(),
  estHour: parseInt(new Date().toLocaleTimeString(
    'en-US',
    { timeZone: 'America/New_York', hour: '2-digit', hour12: false }
  )),

  // Function to derive other dates from today
  get yesterday() {
    return addDays(new Date(this.today), -1);
  },

  get tomorrow() {
    return addDays(new Date(this.today), 1);
  },

  get in90Days() {
    return addDays(new Date(this.today), CONFIG.FUTURE_DAYS_LIMIT);
  }
};


/**
 * Adds a specified number of days to a date
 * @param {Date} date - The starting date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} - The resulting date
 */
function addDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

/**
 * Returns the current date being used for calculations
 * @returns {Date} - Current date
 */
function getToday() {
  return state.today;
}

/**
 * Sets the current date and recalculates dependent dates
 * @param {Date} date - New date to use as "today"
 */
function setToday(date) {
  state.today = date;
  // Other dates will be recalculated via getters
}

/**
 * Create a date in US East timezone, so we cat get the right DOW when we get it in U.S. EST
 * @param {string} dateString - mm/dd/yyyy to use for "date"
 */
function createUSEastDate(dateString) {
  if (!dateString || dateString.length !== 10) return null;

  try {
    const date = new Date(`${dateString}` + state.estOffset);
    return date;
  } catch (e) {
    return null;
  }
}

/**
 * Sets the shipping ZIP code and updates delivery flags
 * @param {string} zip - The shipping ZIP code
 */
function setShipToZIP(zip) {
  state.shipToZipcode = zip;
  state.pickup = false;
  state.localDelivery = false;
  state.stateInitial = "";
  state.fedExDelivery = false;

  // Check for special territories
  if (zip >= '99500' && zip <= '99951') {
    state.stateInitial = "AK";
  } else if (zip >= '96700' && zip <= '96899') {
    state.stateInitial = "HI";
  } else if (zip >= '00600' && zip <= '00999') {
    state.stateInitial = "PR";
  }

  // Set delivery flags based on ZIP
  if (!state.pickup) {
    if (LOCAL_DELIVERY_ZIPS.includes(zip)) {
      state.localDelivery = true;
    } else {
      state.fedExDelivery = true;
    }
  }
}

/**
 * Gets the pickup state (for testing)
 * @returns {boolean} - true/false
 */
function getPickupState() {
  return state.pickup;
}

/**
 * Sets whether the order is for pickup
 * @param {boolean} pickUp - Whether the order is for pickup
 */
function setPickup(pickUp) {
  state.pickup = pickUp;

  // Reset other flags if switching to pickup
  if (pickUp) {
    state.fedExDelivery = false;
  }
}

/**
 * Sets the pickup location and updates delivery flags
 * @param {string} pickupLocation - Pickup location (NYC or Miami)
 */
function setPickupLocation(pickupLocation) {
  if (pickupLocation === null) {
    return;
  }

  state.pickupLocation = pickupLocation;
  state.pickup = true;
  state.fedExDelivery = false;
  state.shipToZipcode = "";
}

/**
 * Gets the EST hour manually (for testing)
 * @returns {number} - Hour in 24-hour format
 */
function getEstHour() {
  return state.estHour;
}

/**
 * Sets the EST hour manually (for testing)
 * @param {number} estHour - Hour in 24-hour format
 */
function setEstHour(estHour) {
  state.estHour = estHour;
}

/**
 * Returns the state/territory initial based on ZIP code
 * @returns {string} - Two-letter state/territory code or empty string
 */
function getStateInitial() {
  return state.stateInitial;
}

/**
 * Determines if a date is a valid delivery day based on:
 * - No past dates
 * - No Sundays for anyone
 * - No Mondays for FedEx
 * - Location-specific restrictions
 * - Holiday restrictions
 * - Cutoff time restrictions
 *
 * @param {Date|string} date - Date to check
 * @returns {boolean} - Whether delivery is possible on this date
 */
function isDeliveryDay(date) {
  // Handle string dates in MM/DD format
  if (typeof date === 'string' && date.length < 6) {
    // date = new Date( `${date}/${state.today.getFullYear()}` + state.estOffset );
    date = createUSEastDate( date );
  }
  if ( typeof date === 'string' && date.length == 10 ) {
    date = new Date( date + state.estOffset );
  }

  // Parse date string if needed
  const timestamp = Date.parse(date);
  if ( isNaN( timestamp ) ) {
    return false;
  }
  // Converting between UTC and EST
  const deliveryDate = new Date( timestamp  );
  const m = deliveryDate.getMonth();
  const d = deliveryDate.getDate();
  const y = deliveryDate.getFullYear();

  // No past dates
  if ( isPastDay(deliveryDate, state.today) ) {
    return false;
  }

  // No Sundays for anyone
  if (deliveryDate.getDay() === 0) {
    return false;
  }

  // Check fixed holidays (New Year's, July 4th, Christmas, etc.)
  const dateString = `${m + 1}-${d}`;
  if (CONFIG.HOLIDAYS.CR_FIXED.includes(dateString)) {
    return false;
  }

  if (state.fedExDelivery && CONFIG.HOLIDAYS.FEDEX_FIXED.includes(dateString)) {
    return false;
  }

  // Check floating holidays (Labor Day, Thanksgiving, etc.)
  const fullDateString = `${m + 1}-${d}-${y}`;
  if (CONFIG.HOLIDAYS.CR_FLOATING.includes(fullDateString)) {
    return false;
  }

  const w = getWeek( deliveryDate );
  const dw = deliveryDate.getDay();
  if ( getFloatingHoliday( m, w, dw, y ) !== null ) {
    return false;
  }

  if (state.fedExDelivery && getFedExFloatingHoliday( m, w, dw, y ) !== null ) {
    return false;
  }

  // Check same-day/next-day restrictions based on cutoff times
  const today = new Date(state.today);
  const tomorrow = state.tomorrow;

  if (isSameDay(deliveryDate, today)) {
    // Today's order - check against cutoff times
    if ((state.localDelivery || state.pickup) &&
        state.estHour >= CONFIG.CUTOFF_TIMES.PICKUP) {
      return false;
    } else if (state.fedExDelivery) {
      return false; // Never same-day for FedEx
    }
  } else if ( isSameDay( deliveryDate, tomorrow ) ) {
    // Tomorrow's order - check cutoff for FedEx
    if ( state.fedExDelivery && state.estHour >= CONFIG.CUTOFF_TIMES.DELIVERY ) {
      return false;
    } else if ( state.pickup &&
      deliveryDate.getDay() === 6 &&
      state.estHour >= CONFIG.CUTOFF_TIMES.PICKUP ) {
      // No Saturday pickup after Friday 11 AM cutoff
      return false;
    } else if ( state.localDelivery &&
      deliveryDate.getDay() === 6 &&
      state.estHour >= CONFIG.CUTOFF_TIMES.DELIVERY ) {
      // Saturday NYC local delivery uses FedEx, so use 3 PM Friday cutoff
      return false;
    }
  }

  // Location-specific day restrictions
  if (state.pickup) {
    // NYC and Miami pickup have different off days
    const locationOffDays = state.pickupLocation.toLowerCase() === 'nyc'
      ? CONFIG.LOCATION_OFF_DAYS.NYC
      : CONFIG.LOCATION_OFF_DAYS.MIAMI;

    if (isOffDay(deliveryDate, locationOffDays)) {
      return false;
    }
  } else if (state.localDelivery) {
    // NYC local delivery - no Sundays
    if (isOffDay(deliveryDate, [0])) {
      return false;
    }
  } else if (state.stateInitial) {
    // Special territories (AK, HI, PR) - no weekends or Mondays
    if (['AK', 'HI', 'PR'].includes(state.stateInitial)) {
      if (isOffDay(deliveryDate, [0, 1, 5, 6])) {
        return false;
      }
    }
  } else {
    // Standard FedEx delivery - no Sundays or Mondays
    if (isOffDay(deliveryDate, [0, 1])) {
      return false;
    }
  }

  return true;
}

/**
 * Helper to check if a date is a weekend
 * @param { Date; } date - Date to check;
*/
function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/**
 * Helper function to check if a date falls on one of the specified off days
 * @param {Date} date - Date to check
 * @param {number[]} offDays - Array of day indices (0=Sunday, 6=Saturday)
 * @returns {boolean} - Whether the date is an off day
 */
function isOffDay ( date, offDays ) {
  return offDays.includes(date.getUTCDay());
}

/**
 * Helper function to check if two dates represent the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} - Whether the dates represent the same day
 */
function isSameDay ( date1, date2 ) {
  return date1.setHours(0, 0, 0, 0) === date2.setHours(0, 0, 0, 0);
}

/**
 * Helper function to check if day 1 is the day before day 2
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} - Whether the day 1 is the day before day 2
 */
function isPastDay(date1, date2) {
  return date1.setHours(0, 0, 0, 0) < date2.setHours(0, 0, 0, 0);
}

/**
 * Calculates the day before the first possible delivery date
 * @returns {Date} - Day before first possible delivery
 */
function dayBeforeTheFirstDelivery() {
  let dayBeforeFirstDelivery = state.yesterday;

  // Saturday orders - skip to Monday
  if (state.today.getDay() === 6) {
    dayBeforeFirstDelivery = addDays(state.yesterday, 2);
  } else if (state.fedExDelivery &&
             state.today.getDay() > 0 &&
             state.today.getDay() < 6) {
    dayBeforeFirstDelivery = state.today;
  }

  // After cutoff time - move to next day
  if (state.fedExDelivery && state.estHour >= CONFIG.CUTOFF_TIMES.DELIVERY) {
    dayBeforeFirstDelivery = state.tomorrow;
  }

  // Gift card orders have a longer lead time
  if (state.giftCardOnlyOrder) {
    dayBeforeFirstDelivery = addDays(new Date(), 3);
  }

  return dayBeforeFirstDelivery;
}

/**
 * Calculates the first possible delivery date
 * @returns {Date} - First possible delivery date
 */
function theFirstDeliveryDate() {
  let firstDeliveryDate = state.today;

  // Saturday orders - skip to Monday
  if (state.today.getDay() === 6) {
    firstDeliveryDate = addDays(state.yesterday, 3);
  } else if (state.fedExDelivery &&
             state.today.getDay() > 0 &&
             state.today.getDay() < 6) {
    firstDeliveryDate = state.tomorrow;
  }

  // After cutoff time - move to next available day
  if (state.fedExDelivery && state.estHour >= CONFIG.CUTOFF_TIMES.DELIVERY) {
    firstDeliveryDate = addDays(state.today, 2);
  }

  // Gift card orders have a longer lead time
  if (state.giftCardOnlyOrder) {
    firstDeliveryDate = addDays(state.today, 4);
  }

  return firstDeliveryDate;
}

/**
 * Gets an array of dates that should be excluded from the delivery date picker
 * @param {string} pickup - Pickup location or null
 * @param {string} zipCode - Shipping ZIP code
 * @param {boolean} giftCardOnly - Whether the order contains only gift cards
 * @returns {Array} - Dates to exclude in the format expected by Flatpickr
 */
function getExcludedDates ( pickup, zipCode, giftCardOnly, inclusive = true ) {
  // Update state based on parameters
  if ( pickup != null && pickup.length > 0 ) {
    setPickupLocation( pickup );
  }

  if ( zipCode != null && zipCode.length > 0 ) {
    setShipToZIP( zipCode );
  }

  state.giftCardOnlyOrder = giftCardOnly === true;

  // Start with core exclusions
  const firstDeliveryDate = ( inclusive ) ? theFirstDeliveryDate() : dayBeforeTheFirstDelivery();
  const lastDeliveryDate = ( inclusive ) ? state.in90Days : addDays( state.in90Days, -1 );
  const disabledDates = [
    { end: formatDateISO( firstDeliveryDate ) },
    { start: formatDateISO( lastDeliveryDate ) },
    "Sunday"
  ];

  // Add location-specific day exclusions
  if ( state.fedExDelivery ) {
    disabledDates.push( "Monday" );
  }

  if ( [ 'AK', 'HI', 'PR' ].includes( state.stateInitial ) ) {
    disabledDates.push( "Friday", "Saturday" );
  }

  // Check each date in the range and add specific exclusions
  let checkDate = new Date( firstDeliveryDate );
  const offDays = getOffDaysForCurrentMode();

  while ( checkDate <= state.in90Days ) {
    // Skip days we've already excluded by weekday
    if ( !offDays.includes( checkDate.getDay() ) && !isDeliveryDay( checkDate ) ) {
      disabledDates.push( formatDateISO( checkDate ) );
    }
    checkDate = addDays( checkDate, 1 );
  }

  return disabledDates;
}

/**
 * Helper to get off days for current delivery mode
 * @returns {number[]} - Array of day indices (0=Sunday, 6=Saturday)
 */
function getOffDaysForCurrentMode() {
  const offDays = [0]; // Sunday is always off

  if (state.fedExDelivery) {
    offDays.push(1); // FedEx doesn't deliver Mondays
  }

  if (['AK', 'HI', 'PR'].includes(state.stateInitial)) {
    offDays.push(5, 6); // No Friday/Saturday for territories
  }

  return offDays;
}

/**
 * Format date in ISO format (YYYY-MM-DD)
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date
 */
function formatDateISO(date) {
  return date.toLocaleString('sv', { timeZone: 'America/New_York' }).substring(0, 10);
}

/**
 * Format date in US format (mm/dd/yyyy)
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date
 */
function formatDateUSEast(date) {
  return date.toLocaleString('en-US', { timeZone: 'America/New_York' }).substring(0, 10);
}

/**
 * Format day of week in US format (long form)
 * @param {Date} date - Date to format
 * @returns {string} - Date of week in long form
 */
function formatDOWUSEast(date) {
  return date.toLocaleString('en-US', { timeZone: 'America/New_York', weekday: "long" });
}

/**
 * Checks if a ZIP code is eligible for local delivery
 * @param {string} zipCode - ZIP code to check
 * @returns {boolean} - Whether the ZIP is in the local delivery area
 */
function isShipToLocal(zipCode) {
  const zip = zipCode || state.shipToZipcode;

  if (!zip) {
    return false;
  }

  return LOCAL_DELIVERY_ZIPS.includes(zip);
}

/**
 * Gets a warning message when delivery date needs to change due to cutoff times
 * @returns {string|null} - Warning message or null if no warning
 */
function getDateChangeWarningMessage() {
  if (state.pickup && state.estHour >= CONFIG.CUTOFF_TIMES.PICKUP) {
    return "The cutoff time for today's pickup (11 AM) has passed. You can choo316se the next available day or visit our boutique at 538 Madison Ave, btwn 54th & 55th St.";
  }

  if (state.localDelivery && state.estHour >= CONFIG.CUTOFF_TIMES.PICKUP) {
    return "The cutoff time for today's local delivery (11 AM) has passed. You can choose the next available day or visit our boutique at 538 Madison Ave, btwn 54th & 55th St.";
  }

  if (state.fedExDelivery && state.estHour >= CONFIG.CUTOFF_TIMES.DELIVERY) {
    return "The cutoff time for tomorrow's FedEx delivery (3 PM) has passed. Please choose the next available date.";
  }

  return null;
}


function getWeek( date ) {
  var month = date.getMonth()
    , year = date.getFullYear()
    , firstWeekday = new Date( year, month, 1 ).getDay()
    , lastDateOfMonth = new Date( year, month + 1, 0 ).getDate()
    , offsetDate = date.getDate() + firstWeekday - 1
    , index = 0 // start index at 0 or 1, your choice
    , weeksInMonth = index + Math.ceil( ( lastDateOfMonth + firstWeekday - 7 ) / 7 )
    , week = index + Math.floor( offsetDate / 7 )
    ;
  if ( week < 2 + index ) return week;
  return week === weeksInMonth ? index + 5 : week;
};

/**
 * Returns the holiday name for a given month, week, and day
 * @param {number} month - Month (0-11)
 * @param {number} week - Week of the month (0-4)
 * @param {number} day - Day of the week (0-6, 0=Sunday)
 * @returns {string|null} - Holiday name or null if not a holiday
 */
const holidays = { // keys are formatted as month,week,day
  "8,0,1": "Labor Day",
  "10,3,4": "Thanksgiving Day"
};

function getFloatingHoliday ( month, week, day, year = null ) {
  // Check if the first day of the month is before or after the day of the week
  const firstDayOfMonth = ( year === null ) ?
    new Date( new Date().getFullYear(), month, 1 ).getDay() : new Date( year, month, 1 ).getDay();

  if ( firstDayOfMonth > day ) {
    // If the first day of the month is after the day of the week, we need to adjust the week
    week -= 1;
  }

  // Labor Day is always the first Monday in September
  // date: getNthDayOf(1, 1, 9, year)
  // or (0, 1, 8, year) for 0-indexed month, and week

  if ( CONFIG.HOLIDAYS.FLOATING.LABOR_DAY.includes( month + "," + week + "," + day ) ) return "Labor Day";
  if ( CONFIG.HOLIDAYS.FLOATING.THANKSGIVING.includes( month + "," + week + "," + day ) ) return "Thanksgiving Day";
  return null;

}

// The day after floating holidays is a special case for FedEx, the day before has no pickup.
function getFedExFloatingHoliday ( month, week, day, year = null ) {
  // Check if the first day of the month is before or after the day of the week
  const firstDayOfMonth = ( year === null ) ?
    new Date( new Date().getFullYear(), month, 1 ).getDay() : new Date( year, month, 1 ).getDay();

  if ( firstDayOfMonth > day - 1 ) {
    // If the first day of the month is after the day of the week, we need to adjust the week
    week -= 1;
  }

  if ( CONFIG.HOLIDAYS.FEDEX_FLOATING.THE_DAY_AFTER_LABOR_DAY.includes( month + "," + week + "," + day ) ) return "The Day After Labor Day";
  if ( CONFIG.HOLIDAYS.FEDEX_FLOATING.THE_DAY_AFTER_THANKSGIVING.includes( month + "," + week + "," + day ) ) return "The Day After Thanksgiving Day";
  return null;

}

// Export functions for use in other modules and testing
// Uncomment for testing in Theme
// Leave uncommented for checkout UI!!
// module.exports = {
//   addDays,
//   createUSEastDate,
//   dayBeforeTheFirstDelivery,
//   formatDateUSEast,
//   formatDOWUSEast,
//   getDateChangeWarningMessage,
//   getEstHour,
//   getExcludedDates,
//   getFloatingHoliday,
//   getFedExFloatingHoliday,
//   getPickupState,
//   getStateInitial,
//   getToday,
//   getWeek,
//   isDeliveryDay,
//   isShipToLocal,
//   isWeekend,
//   setEstHour,
//   setPickup,
//   setPickupLocation,
//   setShipToZIP,
//   setToday,
//   theFirstDeliveryDate,
// };
